// Rebuilding the bulk wizard from the server, so /drafts/:assignmentId survives a
// refresh. The wizard used to hold pages, routing and extracted text in useState
// only, which meant a reload lost the lot — including files already in storage.
//
// TWO sources, one entry point (`buildResume`). Submissions exist only after the
// Organise commit; before it, the /draftPages snapshot is the only record. Which
// of the two wins is decided here and nowhere else — the step and the data it
// describes must never be read from different sources.
//
// The wizard's position is still not stored anywhere: submission presence,
// fileUrls, extracted text and the snapshot's routing already determine it, and a
// status flag would be one more thing that can disagree with the data.
import { isRichTextEmpty } from '../../Stimulus/stimulusHelpers';
import { normalizeFileUrls } from '../../../utils/normalizeFileUrls';
import { PLACEHOLDER_THUMBNAIL } from './thumbnails';
import { bulkFeedbackPhase } from './feedbackRun';
import {
  fromSnapshotPages,
  filesFromSnapshotPages,
} from './draftPageSnapshot';

export const WIZARD_STEP = {
  UPLOAD: 'upload',
  ORGANISE: 'organise',
  CONVERTING: 'converting',
  REVIEW: 'review',
  GENERATING: 'generating',
};

/**
 * The derived step, translated into the wizard's own step ids.
 *
 * Here rather than at a call site because both routes into the wizard need it,
 * and a second copy is a second thing to update when a step is renamed.
 */
export const FLOW_STEP = {
  [WIZARD_STEP.UPLOAD]: 'dragDrop',
  [WIZARD_STEP.ORGANISE]: 'organise',
  [WIZARD_STEP.CONVERTING]: 'converting',
  [WIZARD_STEP.REVIEW]: 'review',
  [WIZARD_STEP.GENERATING]: 'generating',
};

const answersOf = (submission) =>
  Array.isArray(submission?.answers) ? submission.answers : [];

// Scans past a leading empty answer, unlike answerTextOf which reads the first —
// a resumed draft has to find text wherever it landed.
const textOf = (submission) =>
  answersOf(submission)
    .map((entry) => entry?.answer?.answer ?? entry?.answer?.text ?? '')
    .find((text) => !isRichTextEmpty(text)) || '';

const fileUrlsOf = (submission) =>
  answersOf(submission).flatMap((entry) =>
    normalizeFileUrls(entry?.answer?.fileUrls),
  );

// Routing is what separates "still uploading" from "was organising": pages alone
// only prove files landed. A student key with no pages left is not routing — it
// is a student whose pages were all removed again.
const hasRouting = (snapshot) =>
  Object.values(snapshot?.studentAssignments || {}).some(
    (pageIds) => (pageIds || []).length > 0,
  );

const stepBeforeCommit = (snapshot) =>
  hasRouting(snapshot) ? WIZARD_STEP.ORGANISE : WIZARD_STEP.UPLOAD;

/**
 * Review opens after every response has completed conversion or reported an
 * actionable failure, matching the live batch loading screen on a fresh visit.
 *
 * Submissions always win over the snapshot. The server drops the snapshot when
 * the batch is created, but a crash between the two leaves a stale one behind,
 * and reading that would send a committed draft back to Organise.
 */
export const deriveWizardStep = (submissions, snapshot, assignment, feedbackStatus) => {
  const list = Array.isArray(submissions) ? submissions : [];
  if (list.length === 0) return stepBeforeCommit(snapshot);
  // Manual and peer review use REVIEWED too, without ever starting a JeddAI run.
  // Keep a partially reviewed batch at its conversion/review step so continuing
  // can finish the existing Tasks flow without marking or replacing it again.
  const isManualReview = ['TEACHER', 'P2P'].includes(assignment?.reviewedBy);
  const phase = bulkFeedbackPhase(feedbackStatus);
  if (!isManualReview && (['generating', 'failed', 'ready'].includes(phase) ||
      (phase === 'unknown' && list.some(row => row.feedbackRequestAcceptedAt || row.jeddaiFeedbackReceivedAt ||
      row.jeddaiFailureReason || ['SUBMITTED', 'FEEDBACK_ACCEPTED', 'FEEDBACK_FAILED', 'REVIEWED'].includes(row.status))))) {
    return WIZARD_STEP.GENERATING;
  }
  if (!isManualReview && phase === 'converting') return WIZARD_STEP.CONVERTING;
  return list.every((submission) => answersOf(submission).length > 0 && answersOf(submission).every(({ answer }) =>
    answer?.extractionStatus !== 'EXTRACTING' && (
      ['EXTRACTED', 'FAILED'].includes(answer?.extractionStatus) ||
      !isRichTextEmpty(answer?.answer ?? answer?.text ?? '')
    )))
    ? WIZARD_STEP.REVIEW
    : WIZARD_STEP.CONVERTING;
};

/**
 * Marked, and readable because of it. Mirrors the precedence the status endpoint
 * applies to the same two fields: a failure that arrived after a result still
 * reports as failed, and a failed student has nothing to open.
 */
const feedbackLanded = (submission) =>
  !submission?.jeddaiFailureReason &&
  Boolean(submission?.jeddaiFeedbackReceivedAt);

/**
 * The submission to open instead of resuming the wizard, or null while the draft
 * is still a draft.
 *
 * A bulk draft whose feedback has landed is finished work, not work in progress —
 * seeding the wizard at Review sent the teacher back through steps they had
 * already completed to reach marking they had already paid for.
 *
 * Wait for the whole batch to settle, then open its first readable response,
 * matching the handoff after a newly completed marking run.
 */
export const firstMarkedSubmissionId = (submissions) => {
  const list = Array.isArray(submissions) ? submissions : [];
  return list.length && list.every((s) => feedbackLanded(s) || s.jeddaiFailureReason)
    ? list.find(feedbackLanded)?.id || null : null;
};

// Derived from the submission and the file's position rather than generated, so
// re-hydrating the same draft twice yields the same page ids — a fresh uuid per
// hydration would re-key every React row and discard the routing on each poll.
const pageId = (submissionId, index) => `resumed-${submissionId}-${index}`;

const pagesForSubmission = (submission) => {
  const text = textOf(submission);
  const files = answersOf(submission).flatMap(({ answer }) =>
    normalizeFileUrls(answer?.fileUrls, {
      preserveDuplicates: answer?.filePageIndexes?.length > 0,
    }).map((fileUrl, index) => ({
      fileUrl, sourcePageIndex: answer?.filePageIndexes?.[index] ?? 0,
    })),
  );
  if (!files.length && text) return [{
    id: pageId(submission.id, 0), source: 'manual', content: text,
    extractedText: text, pageNumber: 1,
  }];
  return files.map(({ fileUrl, sourcePageIndex }, index) => ({
    id: pageId(submission.id, index),
    source: 'file',
    fileUrl,
    thumbnail: PLACEHOLDER_THUMBNAIL,
    // Bulk pages may share a stored PDF. Keep the original page within it.
    sourcePageIndex,
    pageNumber: index + 1,
    extractedText: text,
    content: text,
  }));
};

const studentOf = (submission) => ({
  id: submission.studentId,
  name: submission.studentName || submission.name || 'Student',
  studentName: submission.studentName || submission.name || 'Student',
});

/**
 * @returns {{pages: Array, files: Array, studentAssignments: Object, students: Array}}
 * the wizard state that was previously unrecoverable after a reload.
 *
 * `files` is always empty here: the left rail is keyed on fileId, and a submission
 * records only urls. Returned anyway so both hydrators satisfy one contract and a
 * consumer cannot be written against a key only one of them sets.
 */
export const hydrateWizardFromSubmissions = (submissions) => {
  const list = Array.isArray(submissions) ? submissions : [];
  // Three linear passes over one pre-computed pairing. Folding with array and
  // object spreads rebuilt the whole accumulator per submission, which is
  // quadratic in a draft that can legitimately carry 200 students.
  const perStudent = list.map((submission) => [
    submission,
    pagesForSubmission(submission),
  ]);
  return {
    pages: perStudent.flatMap(([, pages]) => pages),
    files: [],
    studentAssignments: Object.fromEntries(
      perStudent.map(([submission, pages]) => [
        submission.studentId,
        pages.map((page) => page.id),
      ]),
    ),
    students: list.map(studentOf),
  };
};

/** Same contract, read from the pre-commit snapshot instead. */
export const hydrateWizardFromSnapshot = (snapshot) => ({
  pages: fromSnapshotPages(snapshot?.pages),
  files: filesFromSnapshotPages(snapshot?.pages),
  studentAssignments: snapshot?.studentAssignments || {},
  students: Object.entries(snapshot?.studentNames || {}).map(([id, name]) => ({ id, name })),
});

const questionOf = (assignment) => assignment?.questions?.[0];

/**
 * The single reading of "what state is this draft in".
 *
 * Submissions win whenever they exist. The server drops the snapshot when the
 * batch is created, but a crash between the two leaves a stale one behind, and
 * reading that would send a committed draft back to Organise.
 *
 * @returns the whole `resume` object the wizard is seeded from.
 */
export const buildResume = ({ submissions, snapshot, assignment, feedbackStatus }) => {
  const list = Array.isArray(submissions) ? submissions : [];
  return {
    ...(list.length
      ? hydrateWizardFromSubmissions(list)
      : hydrateWizardFromSnapshot(snapshot)),
    step: deriveWizardStep(list, snapshot, assignment, feedbackStatus),
    submissionIds: list.map((submission) => submission?.id).filter(Boolean),
    snapshotVersion: snapshot?.version ?? 0,
    title: assignment?.title || '',
    expectedTitle: assignment?.title ?? null,
    question: questionOf(assignment)?.question || '',
    stimulusText: questionOf(assignment)?.stimulusText || '',
    stimulusFiles: questionOf(assignment)?.stimulusFiles || [],
    courseMaterials: (questionOf(assignment)?.courseMaterials || [])
      .filter((ref) => ref.materialId && !ref.pointIds?.length)
      .map((ref) => ({ id: ref.materialId, title: ref.title || 'Course materials' })),
    selectedYear: assignment?.years?.[0] ? { title: assignment.years[0] } : null,
    selectedSubject: assignment?.subjects?.[0] ? {
      title: assignment.subjects[0].subjectName,
      code: assignment.subjects[0].subjectCode,
    } : null,
    selectedTaskType: assignment?.taskType || questionOf(assignment)?.taskType
      ? { title: assignment?.taskType || questionOf(assignment)?.taskType } : null,
    draftSettings: {
      state: assignment?.states?.[0],
      responseType: questionOf(assignment)?.responseType,
      marksOutOf: questionOf(assignment)?.marksOutOf,
      introParagraphs: questionOf(assignment)?.introParagraphs,
      bodyParagraphs: questionOf(assignment)?.bodyParagraphs,
      conclusionParagraphs: questionOf(assignment)?.conclusionParagraphs,
    },
  };
};

/**
 * The same reading for a bulk upload attached to a PUBLISHED assignment — the
 * Tasks flow, `/tasks/:assignmentId/bulk-upload`.
 *
 * Snapshot only, and submissions deliberately unreachable. On a draft every
 * submission belongs to the bulk upload, so buildResume prefers them. A published
 * task also carries responses its students submitted themselves, and reading
 * those as this wizard's committed batch opens Review on another student's work.
 * The one thing this flow needs back is what it was losing: the page list and the
 * routing, both of which live in the pre-commit snapshot.
 *
 * Takes no assignment: the Tasks flow skips the question step, so a title and a
 * question would seed fields it never shows.
 *
 * @returns the `resume` object the wizard is seeded from, stepped in flow ids.
 */
export const buildSnapshotResume = (snapshot) => ({
  ...hydrateWizardFromSnapshot(snapshot),
  step: FLOW_STEP[deriveWizardStep([], snapshot)],
  submissionIds: [],
  snapshotVersion: snapshot?.version ?? 0,
  title: '',
  question: '',
});

/**
 * Whether this draft is being picked back up or opened for the first time.
 *
 * Read off the same hydrated `resume` the wizard is seeded from rather than the
 * derived step: an untouched draft and one whose pages were all deleted again
 * both sit at UPLOAD, and only the second of those has anything to resume.
 */
export const hasDraftProgress = (resume) =>
  Boolean(resume?.pages?.length || resume?.submissionIds?.length);
